import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import { Controller } from 'react-hook-form';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

// Styles
import styles from './ContactForm.module.css';

// Components
import Checkbox from '../../components/atoms/Checkbox/Checkbox';
import TextField from '../../components/atoms/TextField/TextField';

// Services
import { useHowCanHelpTypes, useResidences } from '../../src/services/contact';

const SelectField = dynamic(() => import('../../components/atoms/SelectField/SelectField'), {
  ssr: false,
});

const InputPhoneNumber = dynamic(() => import('../../components/atoms/InputPhoneNumber/InputPhoneNumber'), {
  ssr: false,
});

function ContactForm({
  register, control, errors, children,
}) {
  const { locale } = useRouter();
  const { t } = useTranslation();

  const { data: optionsHelpTypes } = useHowCanHelpTypes();
  const { data: optionsResidences } = useResidences();

  const buildResidencesOptions = (residences) => {
    if (!residences?.length) return [];
    return residences
      .map((residence) => ({
        value: residence.crmId,
        label: residence.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const buildHelpTypesOptions = (helpTypes) => {
    if (!helpTypes?.length) return [];
    return helpTypes.map((helpType) => ({
      value: helpType.code,
      label: helpType.labels?.[locale],
    }));
  };

  const findValue = (options, field) => {
    if (!options) return null;
    return options.find((option) => option.value === field.value);
  };

  return (
    <div className={styles.container}>
      <p className={styles.text}>
        {t('contact.form.emailMode.text')}
      </p>

      <div className={styles.form}>
        <div className={styles.fieldGroup}>
          <TextField
            label={t('contact.form.firstName')}
            error={errors?.firstName?.message && t(errors?.firstName?.message)}
            {...register('firstName')}
          />
          <TextField
            label={t('contact.form.lastName')}
            error={errors?.lastName?.message && t(errors?.lastName?.message)}
            {...register('lastName')}
          />
        </div>
        <div className={styles.fieldGroup}>
          <div className={styles.field}>
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <InputPhoneNumber
                  label={t('contact.form.phone.label')}
                  placeholder={t('contact.form.phone.placeholder')}
                  error={errors?.phone ? t(errors?.phone?.message) : null}
                  {...field}
                />
              )}
            />
          </div>
          <TextField
            type="email"
            error={errors?.email?.message && t(errors?.email?.message)}
            label={t('contact.form.email')}
            {...register('email')}
          />
        </div>
        <div className={styles.fieldGroup}>
          <Controller
            control={control}
            name="residenceCrmId"
            render={({ field }) => {
              const options = buildResidencesOptions(optionsResidences);
              const value = findValue(options, field);
              return (
                <SelectField
                  isSearchable={false}
                  id={field.name}
                  label={`${t(`contact.form.${field.name}`)} (${t('contact.form.optional')})`}
                  placeholder={`${t(`contact.form.${field.name}`)}`}
                  options={options}
                  {...field}
                  onChange={(option) => field.onChange(option.value)}
                  value={value}
                />
              );
            }}
          />
        </div>
        <div className={styles.fieldGroup}>
          <Controller
            control={control}
            name="howCanHelpTypeCode"
            render={({ field }) => {
              const options = buildHelpTypesOptions(optionsHelpTypes);
              const value = findValue(options, field);
              return (
                <SelectField
                  isSearchable={false}
                  id={field.name}
                  label={`${t(`contact.form.${field.name}`)} (${t('contact.form.optional')})`}
                  placeholder={`${t(`contact.form.${field.name}`)}`}
                  options={options}
                  {...field}
                  onChange={(option) => field.onChange(option.value)}
                  value={value}
                />
              );
            }}
          />
        </div>
        <div className={styles.fieldGroup}>
          <TextField
            multiline
            rows={5}
            label={`${t('contact.form.comment')}`}
            error={errors?.comment?.message && t(errors?.comment?.message)}
            placeholder={`${t('contact.form.commentPlaceholder')}`}
            {...register('comment')}
          />
        </div>

        <div className={styles.fieldGroup}>
          <Checkbox
            id="newsletter"
            error={errors?.newsletter?.message && t(errors?.newsletter?.message)}
            label={t('contact.form.newsletter')}
            theme="light"
            {...register('newsletter')}
          />
        </div>
        {children}
      </div>
    </div>
  );
}

ContactForm.propTypes = {
  register: PropTypes.func.isRequired,
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({
    firstName: PropTypes.shape({ message: PropTypes.string }),
    lastName: PropTypes.shape({ message: PropTypes.string }),
    phone: PropTypes.shape({ message: PropTypes.string }),
    email: PropTypes.shape({ message: PropTypes.string }),
    comment: PropTypes.shape({ message: PropTypes.string }),
    newsletter: PropTypes.shape({ message: PropTypes.string }),
  }),
  children: PropTypes.node,
};

ContactForm.defaultProps = {
  errors: {},
  children: null,
};

export default ContactForm;
